import React, { useState, useContext, useEffect, useMemo } from 'react';
import { Button, Modal } from '@sw-sw/lib-ui';
import { ProjectContext } from '../../../contexts/ProjectContext';
import RolesContext from '../../../contexts/RolesContext';
import { useHistory } from 'react-router-dom';
import classnames from 'classnames';
import schedulerApi from '../../../utils/api/inspectionSchedular';
import inspectionApi from '../../../utils/api/inspection';
import { toast } from 'react-toastify';
import { FormContextProvider, FormContext, FormSchemaFields } from '@sw-sw/lib-form';
import { UIControlType } from '@sw-sw/lib-form-control-types';
import FormModal from '../../Shared/form/modal/FormModal';

interface UpcomingInspection {
  id?: number;
  project_id: number;
  template_id: number;
  template_name: string;
  template_form_type?: string | null;
  date_of_inspection: string;
  inspection_type: string;
  inspector_id: number | null;
  inspector_name: string | null;
  status: string;
  is_existing: boolean;
  scheduled_inspection?: boolean;
  notes?: string;
}

interface ApiResponse {
  success: boolean;
  data: UpcomingInspection[];
  count: number;
  message: string;
}

interface UpcomingInspectionSectionProps {
  className?: string;
  refreshToken?: number;
  onInspectionDeleted?: () => void;
}

const InspectionDeletionForm: React.FC<{ onSubmit: (formData: { reason: string }) => Promise<void>; onCancel: () => void }> = ({ onSubmit, onCancel }) => {
  const deletionSchema = useMemo(() => {
    return {
      reason: {
        controlType: UIControlType.textarea,
        rows: 4,
        validation: {
          required: true,
        },
      },
    };
  }, []);

  return (
    <FormContextProvider initialValue={{ reason: '' }}>
      <FormContext.Consumer>
        {formContext => {
          const isSubmitDisabled = !formContext.value.reason || !formContext.value.reason.trim();

          return (
            <FormModal
              modalProps={{
                title: 'Are you sure you want to cancel / delete this inspection?',
                submitBtnText: 'Delete',
                disableSubmit: isSubmitDisabled,
              }}
              onCancel={onCancel}
              onSubmit={async () => await onSubmit({ reason: formContext.value.reason })}
            >
              <div style={{ marginBottom: '1rem' }}>
                <strong>The inspection will be hidden from all views. This action cannot be reverted.</strong>
              </div>

              <FormSchemaFields
                schema={deletionSchema}
                formData={formContext.value}
                onChange={formContext.set}
                children={<div style={{ marginBottom: '1rem' }}><strong>Please provide a reason as to why this inspection is being canceled / deleted:</strong></div>}
              />
            </FormModal>
          );
        }}
      </FormContext.Consumer>
    </FormContextProvider>
  );
};

const UpcomingInspectionSection: React.FC<UpcomingInspectionSectionProps> = ({
  className,
  refreshToken = 0,
  onInspectionDeleted,
}) => {
  const history = useHistory();
  const projectStore = useContext(ProjectContext);
  const permCheck = useContext(RolesContext).userHasPermission;
  const [editingDueDate, setEditingDueDate] = useState<number | null>(null);
  const [newDueDate, setNewDueDate] = useState<string>('');
  const [showAll, setShowAll] = useState<boolean>(false);
  const [upcomingInspections, setUpcomingInspections] = useState<UpcomingInspection[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [primaryTemplate, setPrimaryTemplate] = useState<UpcomingInspection | null>(null);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [cancelInspection, setCancelInspection] = useState<UpcomingInspection | null>(null);
  const [cancelReason, setCancelReason] = useState<string>('');
  const [startingInspectionId, setStartingInspectionId] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteInspection, setDeleteInspection] = useState<UpcomingInspection | null>(null);

  const toMidnight = (d: Date | string) => {
    const date = typeof d === 'string' ? new Date(d) : new Date(d);
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = date.getUTCDate();
    const localDate = new Date(year, month, day);
    localDate.setHours(0, 0, 0, 0);
    return localDate;
  };
  const todayMidnight = () => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  };

  const fetchUpcomingInspections = async () => {
    if (!projectStore.project?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      const data: ApiResponse = await schedulerApi.getUpcomingInspections(projectStore.project.id);

      if (data.success) {
        const filteredInspections = data.data
          .filter((inspection: UpcomingInspection) => inspection.status !== 'complete' && inspection.status !== 'cancelled')
          .sort((a: UpcomingInspection, b: UpcomingInspection) => {
            const dateA = new Date(a.date_of_inspection).getTime();
            const dateB = new Date(b.date_of_inspection).getTime();
            return dateA - dateB;
          });

        setUpcomingInspections(filteredInspections);
        const primary = data.data.find((inspection: any) => inspection.is_primary === true);
        if (primary) {
          setPrimaryTemplate(primary);
        }
      } else {
        setError('Failed to fetch upcoming inspections');
      }
    } catch (err) {
      setError('Error fetching upcoming inspections');
      console.error('Error fetching upcoming inspections:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUpcomingInspections();
  }, [projectStore.project?.id, refreshToken]);

  const getInspectionId = (inspection: UpcomingInspection): number => {
    return inspection.id || inspection.template_id || 0;
  };

  const getFormType = (inspection: UpcomingInspection): string => {
    return inspection.template_form_type || inspection.template_name || 'N/A';
  };

  const getInspectorName = (inspection: UpcomingInspection): string => {
    return inspection.inspector_name || 'Unassigned';
  };

  const getInspectionType = (inspection: UpcomingInspection): string => {
    return inspection.inspection_type || 'N/A';
  };

  const getInspectionDate = (inspection: UpcomingInspection): string => {
    return inspection.date_of_inspection;
  };

  const handleStartInspection = async (inspection: UpcomingInspection) => {
    const inspectionId = getInspectionId(inspection);
    try {
      if (!projectStore.project?.id) {
        console.error('Project ID is required to start inspection');
        return;
      }
      if (!inspection.id) {
        console.error('Junction ID is required to start inspection');
        return;
      }
      setStartingInspectionId(inspectionId);
      const validInspectionTypes = [
        'Routine',
        'Routine + Post Storm',
        'Post Storm',
        'Final',
        'Other',
        'Pre-Qualifying Precipitation Event',
        'During Qualifying Precipitation Event',
        'Post-Qualifying Precipitation Event',
        'Before Release of Contained Storm Water',
        'During Release of Contained Storm Water',
        'Quarterly Non-Storm Water'
      ];
      let inspectionType = inspection.inspection_type;
      if (!validInspectionTypes.includes(inspectionType)) {
        console.warn(`Invalid inspection type "${inspectionType}", defaulting to "Routine"`);
        inspectionType = 'Routine';
      }
      const requestBody = {
        junctionId: inspection.id,
        projectId: projectStore.project.id,
        inspection_types: [inspectionType]
      };
      console.log('Creating inspection with:', requestBody);
      const data = await inspectionApi.createWithJunction(requestBody);
      if (data && data.id) {
        history.push(`/inspection/${data.id}/questionnaire`);
        await fetchUpcomingInspections();
      } else {
        console.error('Failed to create inspection: No inspection ID returned');
        setStartingInspectionId(null);
      }
    } catch (error) {
      console.error('Error starting inspection:', error);
      setStartingInspectionId(null);
      toast.error('Failed to start inspection. Please try again.');
    }
  };

  const handleEditDueDate = (inspection: UpcomingInspection) => {
    const inspectionId = getInspectionId(inspection);
    const currentDate = getInspectionDate(inspection);
    setEditingDueDate(inspectionId);
    const date = new Date(currentDate);
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    const localDateString = `${year}-${month}-${day}`;
    setNewDueDate(localDateString);
  };

  const handleSaveDueDate = async (inspection: UpcomingInspection) => {
    try {
      if (!inspection.id) {
        console.error('Inspection ID is required to update inspection date');
        return;
      }
      if (!newDueDate) {
        console.error('New due date is required');
        return;
      }
      const response = await schedulerApi.updateNextInspectionDate(inspection.id, {
        next_inspection_date: newDueDate
      });
      if (response.success) {
        console.log('Next inspection date updated successfully:', response.message);
        setEditingDueDate(null);
        setNewDueDate('');
        await fetchUpcomingInspections();
      } else {
        console.error('Failed to update next inspection date:', response.message);
      }
    } catch (error) {
      console.error('Error updating due date:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingDueDate(null);
    setNewDueDate('');
  };

  const handleOpenCancelModal = (inspection: UpcomingInspection) => {
    setCancelInspection(inspection);
    setCancelReason('');
    setShowCancelModal(true);
  };

  const handleCloseCancelModal = () => {
    setShowCancelModal(false);
    setCancelInspection(null);
    setCancelReason('');
  };

  const handleCancelInspection = async () => {
    if (!cancelInspection || !cancelInspection.id || !projectStore.project?.id) {
      toast.error('Missing required information to cancel inspection');
      return;
    }
    if (!cancelReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }
    try {
      const validInspectionTypes = [
        'Routine',
        'Routine + Post Storm',
        'Post Storm',
        'Final',
        'Other',
        'Pre-Qualifying Precipitation Event',
        'During Qualifying Precipitation Event',
        'Post-Qualifying Precipitation Event',
        'Before Release of Contained Storm Water',
        'During Release of Contained Storm Water',
        'Quarterly Non-Storm Water'
      ];
      let inspectionType = cancelInspection.inspection_type;
      if (!validInspectionTypes.includes(inspectionType)) {
        console.warn(`Invalid inspection type "${inspectionType}", defaulting to "Routine"`);
        inspectionType = 'Routine';
      }
      const requestBody = {
        projectId: projectStore.project.id,
        junctionId: cancelInspection.id,
        deletion_reason: cancelReason,
        inspection_types: [inspectionType]
      };
      await inspectionApi.cancelOverdue(requestBody);
      toast.success('Inspection cancelled successfully');
      setUpcomingInspections(prev => prev.filter(ins => ins.id !== cancelInspection.id));
      handleCloseCancelModal();
      onInspectionDeleted?.();
      await Promise.all([fetchUpcomingInspections(), projectStore.inspectionsQuery.refetch()]);
    } catch (error) {
      console.error('Error cancelling inspection:', error);
      toast.error('Failed to cancel inspection');
    }
  };

  const handleOpenDeleteModal = (inspection: UpcomingInspection) => {
    setDeleteInspection(inspection);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteInspection(null);
  };

  const handleDeleteInspection = async (formData: { reason: string }) => {
    if (!deleteInspection || !deleteInspection.id) {
      toast.error('Missing required information to delete inspection');
      return;
    }
    try {
      await inspectionApi.destroy(deleteInspection.id, formData.reason);
      toast.success('Inspection deleted successfully');
      setUpcomingInspections(prev => prev.filter(ins => ins.id !== deleteInspection.id));
      handleCloseDeleteModal();
      onInspectionDeleted?.();
      await Promise.all([fetchUpcomingInspections(), projectStore.inspectionsQuery.refetch()]);
    } catch (error) {
      console.error('Error deleting inspection:', error);
      toast.error('Failed to delete inspection');
      handleCloseDeleteModal();
      await Promise.all([fetchUpcomingInspections(), projectStore.inspectionsQuery.refetch()]);
    }
  };

  const handleOpenInspection = (inspectionId: number) => {
    history.push(`/inspection/${inspectionId}/questionnaire`);
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = date.getUTCDate();
    const localDate = new Date(year, month, day);
    return localDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const today = todayMidnight();
  const sorted = upcomingInspections;
  const dueTodayList = sorted.filter((ins) => toMidnight(ins.date_of_inspection).getTime() === today.getTime());
  const allowedStartInspectionIds: Set<number> = new Set(dueTodayList.map((ins) => getInspectionId(ins)));

  const displayedInspections = showAll ? upcomingInspections : upcomingInspections.slice(0, 3);
  const hasMoreInspections = upcomingInspections.length > 3;

  const handleToggleShowAll = () => {
    setShowAll(!showAll);
  };

  if (isLoading) {
    return (
      <section className={classnames('upcoming-inspection-section', className)}>
        <div className="upcoming-inspection-header">
          <h3>Upcoming Inspections</h3>
        </div>
        <div className="loading-inspections">
          <p>Loading upcoming inspections...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={classnames('upcoming-inspection-section', className)}>
        <div className="upcoming-inspection-header">
          <h3>Upcoming Inspections</h3>
        </div>
        <div className="error-inspections">
          <p>{error}</p>
          <Button
            outline
            onClick={fetchUpcomingInspections}
            className="retry-btn"
          >
            Retry
          </Button>
        </div>
      </section>
    );
  }

  if (upcomingInspections.length === 0) {
    return (
      <section className={classnames('upcoming-inspection-section', className)}>
        <div className="upcoming-inspection-header">
          <h3>Upcoming Inspections</h3>
        </div>
        <div className="no-upcoming-inspections">
          <p>No upcoming inspections scheduled.</p>
        </div>
      </section>
    );
  }

  return (
    <section className={classnames('upcoming-inspection-section', className)}>
      <div className="upcoming-inspection-header">
        <h3>Upcoming Inspections</h3>
      </div>

      <div className="upcoming-inspection-table-container">
        <table className="upcoming-inspection-table">
          <thead>
            <tr>
              <th>Form Type</th>
              <th>Assigned Inspector</th>
              <th>Inspection Type</th>
              <th>Due Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayedInspections.map((inspection: UpcomingInspection, index: number) => {
              const inspectionId = getInspectionId(inspection);
              const inspectionDate = getInspectionDate(inspection);
              const canEditThis = permCheck('update', 'Inspection') && inspection.id != null;
              const canStartThis = allowedStartInspectionIds.has(inspectionId) && permCheck('update', 'Inspection');
              const isOverdue = toMidnight(inspectionDate) < today && inspection.status === 'overdue';
              const isStarting = startingInspectionId === inspectionId;
              const isExistingInspection = inspection.inspection_type === 'inspection';

              return (
                <tr key={inspectionId || index}>
                  <td data-label="Form Type">
                    <div className="form-type-container" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>{getFormType(inspection)}</span>
                      {inspection.scheduled_inspection && (
                        <span
                          className="scheduled-marker"
                          title="Scheduled Inspection"
                          style={{
                            backgroundColor: '#28a745',
                            color: 'white',
                            fontSize: '10px',
                            padding: '2px 6px',
                            borderRadius: '12px',
                            fontWeight: 'bold',
                            textTransform: 'uppercase'
                          }}
                        >
                          S
                        </span>
                      )}
                      {inspection.notes && inspection.notes.trim() && (
                        <span
                          className="notes-icon"
                          title={inspection.notes}
                          style={{
                            backgroundColor: '#007bff',
                            color: 'white',
                            fontSize: '12px',
                            padding: '2px 6px',
                            borderRadius: '50%',
                            cursor: 'help',
                            fontWeight: 'bold',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          📝
                        </span>
                      )}
                    </div>
                  </td>
                  <td data-label="Assigned Inspector">{getInspectorName(inspection)}</td>
                  <td data-label="Inspection Type">{getInspectionType(inspection)}</td>
                  <td data-label="Due Date">
                    {editingDueDate === inspectionId ? (
                      <div className="due-date-edit">
                        <input
                          type="date"
                          value={newDueDate}
                          onChange={(e) => setNewDueDate(e.target.value)}
                          className="due-date-input"
                          min={(() => {
                            const today = new Date();
                            const year = today.getFullYear();
                            const month = String(today.getMonth() + 1).padStart(2, '0');
                            const day = String(today.getDate()).padStart(2, '0');
                            return `${year}-${month}-${day}`;
                          })()}
                        />
                        <div className="due-date-actions">
                          <Button
                            onClick={() => handleSaveDueDate(inspection)}
                            className="save-btn"
                          >
                            Save
                          </Button>
                          <Button
                            outline
                            onClick={handleCancelEdit}
                            className="cancel-btn"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="due-date-display">
                        <span>{formatDate(inspectionDate)}</span>
                        {!isExistingInspection && permCheck('update', 'Inspection') && (
                          <Button
                            outline
                            onClick={() => canEditThis && handleEditDueDate(inspection)}
                            className="edit-date-btn"
                            disabled={!canEditThis}
                          >
                            Edit
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                  <td data-label="Actions">
                    <div className="action-buttons-container">
                      {isExistingInspection ? (
                        <>
                          <Button
                            onClick={() => handleOpenInspection(inspectionId)}
                            className="open-inspection-btn primary"
                            disabled={!permCheck('update', 'Inspection')}
                          >
                            Open Inspection
                          </Button>
                          {permCheck('delete', 'Inspection') && (
                            <Button
                              onClick={() => handleOpenDeleteModal(inspection)}
                              className="delete-inspection-btn danger"
                              outline
                              style={{ marginLeft: '0.5rem' }}
                            >
                              Delete
                            </Button>
                          )}
                        </>
                      ) : isOverdue ? (
                        <>
                          <Button
                            onClick={() => handleStartInspection(inspection)}
                            className="overdue-btn danger"
                            disabled={!inspection.id || !permCheck('update', 'Inspection') || isStarting}
                          >
                            {isStarting ? 'Starting...' : (inspection.id ? 'Start Overdue Inspection' : 'Overdue')}
                          </Button>
                          {inspection.id && permCheck('update', 'Inspection') && (
                            <Button
                              onClick={() => handleOpenCancelModal(inspection)}
                              className="cancel-overdue-btn warning"
                              outline
                              style={{ marginLeft: '0.5rem' }}
                              disabled={isStarting}
                            >
                              Cancel
                            </Button>
                          )}
                        </>
                      ) : canStartThis ? (
                        <>
                          <Button
                            onClick={() => handleStartInspection(inspection)}
                            className="open-inspection-btn primary"
                            disabled={!permCheck('update', 'Inspection') || isStarting}
                          >
                            {isStarting ? 'Starting...' : 'Start Inspection'}
                          </Button>
                          {inspection.id && permCheck('update', 'Inspection') && (
                            <Button
                              onClick={() => handleOpenCancelModal(inspection)}
                              className="cancel-inspection-btn warning"
                              outline
                              style={{ marginLeft: '0.5rem' }}
                              disabled={isStarting}
                            >
                              Cancel
                            </Button>
                          )}
                        </>
                      ) : (
                        <>
                          <Button outline disabled className="scheduled-btn">
                            Scheduled
                          </Button>
                          {inspection.id && permCheck('update', 'Inspection') && (
                            <Button
                              onClick={() => handleOpenCancelModal(inspection)}
                              className="cancel-scheduled-btn warning"
                              outline
                              style={{ marginLeft: '0.5rem' }}
                            >
                              Cancel
                            </Button>
                          )}
                          {primaryTemplate && primaryTemplate.id && permCheck('update', 'Inspection') && (
                            <Button
                              onClick={() => handleStartInspection(primaryTemplate)}
                              className="add-new-inspection-btn secondary"
                              style={{ marginLeft: '0.5rem' }}
                            >
                              Add New Inspection
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {hasMoreInspections && (
        <div className="upcoming-inspection-show-more">
          <Button
            outline
            onClick={handleToggleShowAll}
            className="show-more-btn"
          >
            {showAll
              ? `Show Less (${displayedInspections.length - 3} fewer)`
              : `Show More (${upcomingInspections.length - 3} more)`}
          </Button>
        </div>
      )}

      {showCancelModal && cancelInspection && (
        <Modal
          show={showCancelModal}
          title="Cancel Inspection"
          handleClose={handleCloseCancelModal}
          handleSubmit={handleCancelInspection}
          submitBtnText="Confirm Cancellation"
          cancelBtn={true}
          cancelBtnText="Close"
        >
          <div style={{ padding: '1rem 0' }}>
            <p style={{ marginBottom: '1rem', color: '#333' }}>
              Are you sure you want to cancel this inspection?
            </p>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong>Form Type:</strong> {getFormType(cancelInspection)}
            </div>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong>Inspection Type:</strong> {getInspectionType(cancelInspection)}
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <strong>Due Date:</strong> {formatDate(getInspectionDate(cancelInspection))}
            </div>
            <div>
              <label htmlFor="cancelReason" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Reason for Cancellation: <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <textarea
                id="cancelReason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Please provide a reason for cancelling this inspection..."
                style={{
                  width: '100%',
                  minHeight: '100px',
                  padding: '0.5rem',
                  border: '1px solid #ced4da',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  resize: 'vertical'
                }}
                required
              />
            </div>
          </div>
        </Modal>
      )}

      {showDeleteModal && deleteInspection && (
        <InspectionDeletionForm
          onSubmit={handleDeleteInspection}
          onCancel={handleCloseDeleteModal}
        />
      )}
    </section>
  );
};

export default UpcomingInspectionSection;
